import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ClientLogo } from '../components/ClientLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, PencilSimple, Trash, UploadSimple, Buildings, CircleNotch } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PRESET_COLORS = ['#4f46e5', '#e11d48', '#059669', '#d97706', '#0891b2', '#7c3aed', '#db2777', '#0f172a'];

export default function ClientsPage() {
  const { API, getOrgHeaders, currentOrg } = useAuth();
  const h = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  const canManage = ['owner', 'admin', 'project_manager'].includes(currentOrg?.role);
  const canDelete = ['owner', 'admin'].includes(currentOrg?.role);
  const navigate = useNavigate();

  // Guard direct-URL access: contributors/viewers/customers cannot see clients.
  useEffect(() => {
    if (currentOrg && !canManage) navigate('/dashboard', { replace: true });
  }, [currentOrg, canManage, navigate]);

  const load = useCallback(async () => {
    // Never fetch for non-managers (they get redirected); avoids a 403 toast.
    if (!['owner', 'admin', 'project_manager'].includes(currentOrg?.role)) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients`, h());
      setClients(res.data || []);
    } catch (_) { toast.error('Failed to load clients'); }
    finally { setLoading(false); }
  }, [API, h, currentOrg]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setName(''); setColor(PRESET_COLORS[0]); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setName(c.name); setColor(c.brand_color || PRESET_COLORS[0]); setDialogOpen(true); };

  const save = async () => {
    if (!name.trim()) { toast.error('Client name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/clients/${editing.client_id}`, { name: name.trim(), brand_color: color }, h());
        toast.success('Client updated');
      } else {
        await axios.post(`${API}/clients`, { name: name.trim(), brand_color: color }, h());
        toast.success('Client created');
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (client, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be under 2 MB'); return; }
    setUploadingId(client.client_id);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      await axios.post(`${API}/clients/${client.client_id}/logo`, fd, { ...h(), headers: { ...getOrgHeaders() } });
      toast.success('Logo updated');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Logo upload failed'); }
    finally { setUploadingId(null); }
  };

  const remove = async (client) => {
    if (!window.confirm(`Delete client "${client.name}"? Its projects will be unassigned (not deleted).`)) return;
    try {
      await axios.delete(`${API}/clients/${client.client_id}`, h());
      toast.success('Client deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="clients-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-zinc-900 tracking-tight flex items-center gap-2">
            <Buildings size={24} weight="duotone" className="text-indigo-600" /> Clients
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Brand each customer with a logo &amp; colour. Assign projects to a client and the branding shows on every board.</p>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-client-btn">
            <Plus size={16} className="mr-1.5" /> New client
          </Button>
        )}
      </div>

      {loading ? (
        <div className="py-20 flex items-center justify-center text-zinc-400 gap-2"><CircleNotch className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : clients.length === 0 ? (
        <div className="py-20 text-center">
          <Buildings size={48} weight="thin" className="mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500 text-sm">No clients yet. Create one to start branding your boards.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map(c => (
            <div key={c.client_id} className="bg-white border border-zinc-200 rounded-xl p-5 hover:border-zinc-300 transition-colors" data-testid={`client-card-${c.client_id}`}>
              <div className="flex items-start gap-3">
                <ClientLogo client={c} size={48} rounded="rounded-lg" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-zinc-900 truncate" data-testid={`client-name-${c.client_id}`}>{c.name}</h3>
                  <p className="text-xs text-zinc-400 mt-0.5">{c.project_count} project{c.project_count === 1 ? '' : 's'}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="w-3 h-3 rounded-full ring-1 ring-black/10" style={{ backgroundColor: c.brand_color }} />
                    <span className="text-[11px] text-zinc-400 font-mono">{c.brand_color}</span>
                  </div>
                </div>
              </div>
              {canManage && (
                <div className="flex items-center gap-1.5 mt-4 pt-3 border-t border-zinc-100">
                  <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" hidden
                    ref={el => { fileRefs.current[c.client_id] = el; }}
                    onChange={e => { uploadLogo(c, e.target.files?.[0]); e.target.value = ''; }} />
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={uploadingId === c.client_id}
                    onClick={() => fileRefs.current[c.client_id]?.click()} data-testid={`client-upload-${c.client_id}`}>
                    {uploadingId === c.client_id ? <CircleNotch className="w-3 h-3 mr-1 animate-spin" /> : <UploadSimple size={13} className="mr-1" />}
                    {c.has_logo ? 'Replace logo' : 'Upload logo'}
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(c)} data-testid={`client-edit-${c.client_id}`}>
                    <PencilSimple size={14} />
                  </Button>
                  {canDelete && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600" onClick={() => remove(c)} data-testid={`client-delete-${c.client_id}`}>
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit client' : 'New client'}</DialogTitle>
            <DialogDescription>Set the customer name and brand colour. Upload a logo after saving.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Client name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Acme Corp" className="mt-1" data-testid="client-name-input" autoFocus />
            </div>
            <div>
              <Label className="text-xs">Brand colour</Label>
              <div className="flex items-center gap-2 mt-1.5">
                {PRESET_COLORS.map(c => (
                  <button key={c} onClick={() => setColor(c)} style={{ backgroundColor: c }}
                    className={`w-7 h-7 rounded-full ring-2 transition ${color === c ? 'ring-offset-2 ring-zinc-900' : 'ring-transparent'}`}
                    data-testid={`client-color-${c}`} />
                ))}
                <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-7 h-7 rounded cursor-pointer border border-zinc-200" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="client-save-btn">
              {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : (editing ? 'Save changes' : 'Create client')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
